import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { ArrowLeft, FileSignature, Download } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { generateAgreementPDF } from '../../utils/agreementTemplate'
import { casesAPI, practiceAgreementAPI } from '../../services/api'
import PracticeAgreementModal from '../../components/PracticeAgreementModal' // s101 (D496)
import formatAddress from '../../utils/formatAddress'
import orellaLogoWhite from '../../assets/logos/orella_white_logo.png'
import { formatSex } from '../../utils/formatSex'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// ═══════════════════════════════════════════════════════
// CareAgreementScreen v180.1.0
// Full inline rendering of signed Patient Care Agreement
// Same template as MD Panel PDF — rendered via iframe srcdoc
// Accessible from CaseHome Toolkit chip
// ═══════════════════════════════════════════════════════

export default function CareAgreementScreen() {
  // s101 (D496): the physician's own agreement, signed with Orella's, opens from here too.
  const [showPractice, setShowPractice] = useState(false)
  const navigate = useNavigate()
  const { currentCase } = useCaseStore()
  const { profile } = useProfileStore()
  const { t } = useI18n()
  const [iframeHeight, setIframeHeight] = useState(2400)

  // v225.0.x: this screen renders the legally-signed agreement. The list-derived
  // currentCase can lag or omit the heavy signature, so fetch the authoritative
  // full case when the signature isn't already present and prefer that copy.
  const [fetchedAgreement, setFetchedAgreement] = useState(null)
  const agreement = fetchedAgreement || currentCase?.patientAgreement || currentCase?.patient_agreement
  const signedAt = agreement?.signedAt || agreement?.signed_at
  const signature = agreement?.signature
  const isPreview = !signature || !signedAt

  useEffect(() => {
    const id = currentCase?.id
    if (!id || String(id).startsWith('local-')) return
    if (currentCase?.patientAgreement?.signature) return  // already complete
    let cancelled = false
    casesAPI.get(id)
      .then((full) => {
        if (cancelled || !full) return
        const a = full.patientAgreement || full.patient_agreement
        if (a?.signature) setFetchedAgreement(a)
      })
      .catch((err) => console.warn('[CareAgreement] full-case fetch failed:', err))
    return () => { cancelled = true }
  }, [currentCase?.id])

  const formatDateTime = (iso) => {
    if (!iso) return 'N/A'
    try {
      const d = new Date(iso)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } catch { return iso }
  }

  const formatDate = (iso) => {
    if (!iso) return 'N/A'
    try { return new Date(iso).toISOString().split('T')[0] } catch { return iso }
  }

  // Build the same HTML the MD panel generates for PDF
  const agreementHTML = useMemo(() => {
    if (!currentCase) return ''

    const patientName = `${profile?.firstName || profile?.identification?.firstName || ''} ${profile?.lastName || profile?.identification?.lastName || ''}`.trim() || 'Patient'
    const patientDOB = formatDate(profile?.identification?.dob || profile?.dob || profile?.identification?.dateOfBirth)
    const patientSex = formatSex(profile?.gender || profile?.sex || profile?.identification?.sex) || 'N/A'
    const patientOrlId = profile?.id?.slice(-8) || currentCase.id?.slice(-8) || 'N/A'

    // Doctor / practice info — prefer the full doctor record carried on the case
    const d = currentCase.doctor || {}
    const doctorName = currentCase.doctorName || currentCase.doctor_name || ''
    const parts = doctorName.replace(/^Dr\.?\s*/i, '').split(' ')
    const fullAddress = [
      formatAddress(d.practice_address),
      [d.practice_city, d.practice_state].filter(Boolean).join(', '),
      d.practice_zip
    ].filter(Boolean).join(', ')
    const doctor = {
      firstName: d.first_name || parts[0] || '',
      lastName: d.last_name || parts.slice(1).join(' ') || '',
      credentials: d.credentials || 'MD',
      practiceName: currentCase.practiceName || currentCase.practice_name || d.practice_name || '',
      practiceAddress: fullAddress,
      practicePhone: d.practice_phone || d.phone || '',
      practiceFax: d.practice_fax || '',
      practiceEmail: d.practice_email || '',
      practiceState: d.practice_state || '',
      practiceCity: d.practice_city || '',
      // TF b34 #8: the practice's own cash prices. Absent from the API payload
      // today, in which case generateAgreementV2 falls back to $99/$249 exactly
      // as before — so this line changes nothing until an MD enters a number.
      // ★ v242.151.0 AGREEMENT-FEE-FIELD-1 — the names the MD side actually
      //   writes. `virtual_consult_fee` / `in_office_consult_fee` exist nowhere
      //   in the API or the panel, so this always fell through to the template's
      //   $99/$249 — which happen to be the same numbers as the stored rates,
      //   which is why the document looked correct while reading nothing.
      virtualFee: d.self_pay_virtual_rate != null ? d.self_pay_virtual_rate : d.selfPayVirtualRate,
      inOfficeFee: d.self_pay_in_office_rate != null ? d.self_pay_in_office_rate : d.selfPayInOfficeRate,
      // v242.239.0 (TEST 2.21): only a v2 record prints these fees; v3 carries no pricing, v1 its own.
    }

    try {
      return generateAgreementPDF({
        patientName,
        patientDOB,
        patientSex,
        patientId: patientOrlId,
        caseId: currentCase.id?.slice(-12) || 'N/A',
        signatureData: signature || '',
        signedDate: formatDateTime(signedAt),
        doctor,
        agreementVersion: agreement?.agreementVersion,
        // P2 repair 2 (NIT-1): V3 only when the record says it signed the hardcoded V3 text.
        hardcodedTemplate: agreement?.hardcodedTemplate,
        // v225.3.0: when the signed record carries a sections snapshot
        // (web-link signatures), render that exact content; else fall through
        // to the version-aware hardcoded template.
        title: agreement?.title,
        sections: agreement?.sections,
        effectiveDate: agreement?.effectiveDate || agreement?.effective_date,
      })
    } catch (err) {
      console.error('[CareAgreement] Error generating HTML:', err)
      return ''
    }
  }, [currentCase, profile, signature, signedAt, fetchedAgreement])

  // v239.1.0: render the signed agreement to PDF via the print dialog,
  // mirroring the medical-note download. Uses the standalone agreement HTML
  // (without the iframe height-reporter script).
  const handleDownloadPDF = () => {
    if (!agreementHTML) return
    const w = window.open('', '_blank')
    if (!w) {
      alert(t('errors2.popupsForPdf') || 'Please allow pop-ups to download the PDF.')
      return
    }
    w.document.write(agreementHTML)
    w.document.close()
    setTimeout(() => { w.print() }, 500)
  }

  // Resize iframe to fit content
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type === 'agreementHeight' && e.data.height) {
        setIframeHeight(e.data.height + 40)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Inject height reporter script into the HTML
  const htmlWithReporter = useMemo(() => {
    if (!agreementHTML) return ''
    const script = `<script>
      window.addEventListener('load', function() {
        var h = document.body.scrollHeight;
        parent.postMessage({ type: 'agreementHeight', height: h }, '*');
      });
    <\/script>`
    return agreementHTML.replace('</body>', script + '</body>')
  }, [agreementHTML])

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  if (!currentCase) return <Navigate to="/home" replace />

  return (
    <div className="min-h-full bg-[#F8FAFB] flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-5 flex-shrink-0 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #15354F 0%, #1E4A6E 60%, #245A80 100%)', paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
        <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full" style={{ background: 'rgba(70,168,249,0.08)' }} />
        <div className="absolute -bottom-5 -left-5 w-20 h-20 rounded-full" style={{ background: 'rgba(167,217,252,0.06)' }} />
        <div className="flex items-center justify-between mb-4 relative z-10">
          <button onClick={() => navigate('/case/home')} className="p-1.5 -ml-1.5">
            <ArrowLeft size={22} className="text-white" />
          </button>
          <img src={orellaLogoWhite} alt="Orella" className="h-[18px] object-contain opacity-70" />
        </div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[1.5px] text-[#A7D9FC]/70 bg-white/10 px-3 py-1 rounded-full border border-white/10 mb-3">
            <FileSignature size={10} />
            {t('caseHome.documents') || 'Documents'}
          </div>
          <h1 className="text-xl font-bold text-white mb-1">{t('share.patientCareAgreement')}</h1>
          <p className="text-[12px] text-white/35">{isPreview ? t('agreement.previewDocument', 'Preview — read only') : t('agreement.signedDocument', 'Signed document')}</p>
        </div>
      </div>

      {/* Agreement Content */}
      <div className="flex-1 overflow-auto">
        {htmlWithReporter ? (
          <iframe
            srcDoc={htmlWithReporter}
            title="Patient Care Agreement"
            className="w-full border-0"
            style={{ height: iframeHeight, minHeight: 800, background: '#fff' }}
            sandbox="allow-scripts"
          />
        ) : (
          <div className="flex items-center justify-center py-20">
            <OrellaSpinner size={24} />
            <span className="ml-2 text-gray-400 text-sm">{t('signAgreement.loadingAgreement', 'Loading agreement...')}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 pb-6 flex-shrink-0 bg-white border-t border-[#E8F1F8] space-y-2.5">
        {agreement?.practiceAgreement?.signed && !isPreview && (
          <button type="button" onClick={() => setShowPractice(true)} data-cta="open-practice-agreement"
            className="w-full rounded-2xl border border-[#E5EDF3] bg-white p-3 text-left flex items-center gap-3">
            <FileSignature size={18} className="text-[#46A8F9] flex-none" />
            <span className="flex-1 min-w-0">
              <b className="block text-[14px] text-[#15354F] truncate">{agreement.practiceAgreement.title || t('agreement.practiceFallback', "Your doctor's patient agreement")}</b>
              <span className="text-[12px] text-[#5E7385]">{t('signAgreement.signed', 'Signed')} · v{agreement.practiceAgreement.agreementVersion || 1}</span>
            </span>
          </button>
        )}
        <PracticeAgreementModal isOpen={showPractice} onClose={() => setShowPractice(false)} agreement={agreement?.practiceAgreement}
          caseId={currentCase?.id} getCasePdf={practiceAgreementAPI.casePdf}
          signatureData={agreement?.practiceAgreement?.signature || ''} t={t} />
        {agreementHTML && !isPreview && (
          <button
            onClick={handleDownloadPDF}
            className="w-full py-3.5 rounded-full bg-[#46A8F9] text-white text-[14px] font-semibold flex items-center justify-center gap-2"
          >
            <Download size={16} />
            {t('caseHome.downloadPdf', { defaultValue: 'Download PDF' })}
          </button>
        )}
        <button
          onClick={() => navigate('/case/home')}
          className="w-full py-3.5 rounded-full border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-2"
        >
          <ArrowLeft size={16} />
          {t('caseHome.backToCaseHome')}
        </button>
      </div>
    </div>
  )
}
